import { useEffect, useState } from "react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { useCreateReport, useUpdateReport, type ReportSummary } from "../api";

/** Create a new report or rename/redescribe an existing one. The dashboard
 *  itself is built via the AI agent (later phase); this is just the header. */
export function ReportFormDialog({
  open,
  onOpenChange,
  report,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report?: ReportSummary | null;
}) {
  const isEdit = !!report;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const create = useCreateReport();
  const update = useUpdateReport();
  const pending = create.isPending || update.isPending;

  useEffect(() => {
    if (open) {
      setTitle(report?.title ?? "");
      setDescription(report?.description ?? "");
    }
  }, [open, report]);

  const submit = async () => {
    const t = title.trim();
    if (!t) return;
    try {
      if (isEdit && report) {
        await update.mutateAsync({
          id: report.id,
          input: { title: t, description: description.trim() || null },
        });
        toast.success("Report updated");
      } else {
        await create.mutateAsync({ title: t, description: description.trim() || null });
        toast.success("Report created");
      }
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error)?.message ?? "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit report" : "New report"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Rename or update the description."
              : "Create a report, then build it by describing the data you want to the AI agent (next phase)."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-title">Title</Label>
            <Input
              id="report-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Monthly ARR dashboard"
              maxLength={255}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-desc">Description</Label>
            <Textarea
              id="report-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this dashboard is for (optional)"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !title.trim()}>
            {pending ? "Saving…" : isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
